import * as React from "react";
import { inject, observer } from "mobx-react";
import { MobxComponent } from "../../../../../mobx/components";
import { Button, IconCircle, OrderIcon, RelativeTime } from "@lib/components";
import { normalizeBookingPausedDates, RestaurantUtils } from "@lib/common";
import { config } from "../../../../../config";
import { withTranslation, WithTranslation } from "react-i18next";
import { RestaurantBookingModal } from "./modal";
import {
	RestaurantBookingCreateModal,
	CW_BOOKING_PREFILL_STORAGE_KEY,
	type CalendarSlotPrefill,
} from "./booking-create-modal";
import { RestaurantWalkInModal } from "./walk-in-modal";
// New Figma UI — only shown when reservations integration is enabled
import { BookingsListView } from "./bookings-list-view";
// Legacy UI — shown when reservations integration is disabled
import { BookingsProtable } from "./bookings-protable";
import { sortBookingsList } from "./bookings-date-range-filter";
import { FaBookOpen, FaCog, FaUserPlus } from "react-icons/fa";
import { UI } from "../../../../../core/ui";
import styled from "styled-components";

const PageHeader = styled.div`
	display: flex;
	align-items: center;
	justify-content: space-between;
	gap: 16px;
	padding: 12px 0;
	margin-bottom: 16px;
	flex-wrap: wrap;
`;

const HeaderLeft = styled.div``;

const PageTitle = styled.p`
	font-size: 16px;
	font-weight: 600;
	color: #4b4f5e;
	margin: 0;
	line-height: 24px;
`;

const PageBreadcrumb = styled.p`
	font-size: 14px;
	color: #6b7270;
	margin: 0;
	line-height: 20px;
`;

const HeaderActions = styled.div`
	display: flex;
	align-items: center;
	gap: 16px;
	flex-wrap: wrap;
`;

/* ── Quick Settings Modal ── */
const QSBackdrop = styled.div`
	position: fixed;
	inset: 0;
	z-index: 1000;
	background: rgba(15, 23, 42, 0.45);
	display: flex;
	align-items: center;
	justify-content: center;
	padding: 16px;
`;

const QSCard = styled.div`
	background: #fff;
	border-radius: 12px;
	box-shadow: 0 20px 50px rgba(0,0,0,0.18);
	width: 100%;
	max-width: 420px;
	overflow: hidden;
`;

const QSHeader = styled.div`
	display: flex;
	align-items: center;
	gap: 12px;
	padding: 16px;
	border-bottom: 1px solid #e9eaeb;
`;

const QSTitle = styled.p`
	font-size: 15px;
	font-weight: 600;
	color: #181d27;
	margin: 0;
	flex: 1;
`;

const QSClose = styled.button`
	width: 28px;
	height: 28px;
	border-radius: 6px;
	border: 1px solid #e9eaeb;
	background: #fff;
	color: #6b7280;
	font-size: 16px;
	cursor: pointer;
	display: flex;
	align-items: center;
	justify-content: center;
	&:hover { background: #f1f5f9; }
`;

const QSBody = styled.div`
	padding: 16px;
	display: flex;
	flex-direction: column;
	gap: 14px;
`;

const QSField = styled.div``;

const QSLabel = styled.label`
	display: block;
	font-size: 11px;
	font-weight: 700;
	text-transform: uppercase;
	letter-spacing: 0.1em;
	color: #6b7280;
	margin-bottom: 6px;
`;

const QSHelp = styled.p`
	font-size: 11px;
	color: #9da4ae;
	margin: 4px 0 0;
`;

const QSSelect = styled.select`
	width: 100%;
	height: 38px;
	border: 1px solid #d5d7da;
	border-radius: 6px;
	padding: 0 28px 0 10px;
	font-size: 14px;
	color: #181d27;
	background: #fff;
	background-image: url("data:image/svg+xml;utf8,<svg fill='%236b7280' height='20' viewBox='0 0 24 24' width='20' xmlns='http://www.w3.org/2000/svg'><path d='M7 10l5 5 5-5z'/></svg>");
	background-position: right 6px center;
	background-repeat: no-repeat;
	-webkit-appearance: none;
	appearance: none;
	box-sizing: border-box;
	cursor: pointer;
	&:focus { outline: none; border-color: #94a3b8; }
`;

const QSInput = styled.input`
	width: 100%;
	height: 38px;
	border: 1px solid #d5d7da;
	border-radius: 6px;
	padding: 0 10px;
	font-size: 14px;
	color: #181d27;
	background: #fff;
	box-sizing: border-box;
	&:focus { outline: none; border-color: #94a3b8; }
`;

const QSFooter = styled.div`
	display: flex;
	gap: 8px;
	justify-content: flex-end;
	padding: 12px 16px;
	border-top: 1px solid #e9eaeb;
`;

const QSBtn = styled.button<{ $primary?: boolean }>`
	height: 34px;
	padding: 0 14px;
	border-radius: 8px;
	font-size: 13px;
	font-weight: 600;
	cursor: pointer;
	border: 1px solid ${(p) => (p.$primary ? "#fb2f2f" : "#d5d7da")};
	background: ${(p) => (p.$primary ? "#fb2f2f" : "#fff")};
	color: ${(p) => (p.$primary ? "#fff" : "#414651")};
	&:hover { filter: brightness(0.95); }
	&:disabled { opacity: 0.6; cursor: not-allowed; }
`;

const ActionBtn = styled.button`
	display: inline-flex;
	align-items: center;
	gap: 6px;
	height: 32px;
	padding: 0 12px;
	border: 1px solid #d5d7da;
	border-radius: 8px;
	background: #fff;
	font-size: 14px;
	font-weight: 600;
	color: #414651;
	cursor: pointer;
	white-space: nowrap;
	transition: background 0.1s;
	&:hover {
		background: #f8f9fa;
	}
`;

type BookingsDashboardView = "list" | "calendar";

function resolveBookingsDashboardView(
	saved: BookingsDashboardView | undefined,
	calendarAllowed: boolean
): BookingsDashboardView {
	if (saved === "calendar" && calendarAllowed) return "calendar";
	return "list";
}

interface Props extends WithTranslation { }
interface State {
	loading: boolean;
	item: T.Schema.Booking.BookingSchema | null;
	createModalOpen: boolean;
	walkInModalOpen: boolean;
	quickSettingsOpen: boolean;
	quickSettingsSaving: boolean;
	qsStrategy: string;
	qsThreshold: string;
	qsTurnMinutes: string;
	qsDashboardView: BookingsDashboardView;
	qsPausedDates: string[];
	qsPauseDateDraft: string;
	calendarReloadSignal: number;
	bookingGuestPrefill?: import("./booking-create-modal").BookingGuestPrefill | null;
	/** Prefill create modal when a calendar empty slot is clicked. */
	calendarSlotPrefill: CalendarSlotPrefill | null;
}


@inject("store") @observer
class RestaurantBookingsClass extends MobxComponent<Props, State> {

	constructor(props: Props) {
		super(props);
		const tb = this.injected.store.restaurant?.settings?.services?.table_booking;
		const bl = tb?.booking_logic;
		this.state = {
			loading: true,
			item: null,
			createModalOpen: false,
			walkInModalOpen: false,
			quickSettingsOpen: false,
			quickSettingsSaving: false,
			qsStrategy: bl?.auto_confirm_strategy || "off",
			qsThreshold: bl?.auto_confirm_threshold != null ? String(bl.auto_confirm_threshold) : "",
			qsTurnMinutes: bl?.default_turn_minutes != null ? String(bl.default_turn_minutes) : "",
			qsDashboardView: bl?.default_dashboard_view === "calendar" ? "calendar" : "list",
			qsPausedDates: normalizeBookingPausedDates(tb?.paused_dates),
			qsPauseDateDraft: "",
			calendarReloadSignal: 0,
			bookingGuestPrefill: null,
			calendarSlotPrefill: null,
		};
	}

	tryConsumeBookingPrefillFromStorage = () => {
		try {
			if (typeof sessionStorage === "undefined") return;
			const raw = sessionStorage.getItem(CW_BOOKING_PREFILL_STORAGE_KEY);
			if (!raw) return;
			sessionStorage.removeItem(CW_BOOKING_PREFILL_STORAGE_KEY);
			const p = JSON.parse(raw) as Partial<{
				name: string;
				email: string;
				phone: string;
			}>;
			this.setState({
				createModalOpen: true,
				calendarSlotPrefill: null,
				bookingGuestPrefill: {
					name: typeof p.name === "string" ? p.name : "",
					email: typeof p.email === "string" ? p.email : "",
					phone: typeof p.phone === "string" ? p.phone : "",
				},
			});
		} catch {
			sessionStorage.removeItem(CW_BOOKING_PREFILL_STORAGE_KEY);
		}
	};

	saveBookingLogic = async (
		patch: Partial<T.Schema.Restaurant.Services.RestaurantServiceTableBooking["booking_logic"]>,
		options?: { closeQuickSettings?: boolean; notify?: boolean }
	) => {
		const { store } = this.injected;
		const r = store.restaurant!;
		const notify = options?.notify !== false;
		if (options?.closeQuickSettings) {
			this.setState({ quickSettingsSaving: true });
		}
		try {
			const bl = {
				...(r.settings.services.table_booking.booking_logic || {}),
				...patch,
			};
			const res = await store.api.restaurant_update({
				_id: r._id,
				update: { $set: { "settings.services.table_booking.booking_logic": bl } },
			});
			if (res.outcome) {
				if (notify) UI.notification.error(res.message || "Failed to save");
				return false;
			}
			r.settings.services.table_booking.booking_logic = bl as any;
			if (notify) UI.notification.success("Settings saved");
			if (options?.closeQuickSettings) {
				this.setState({ quickSettingsOpen: false });
			}
			return true;
		} catch (e) {
			if (notify) UI.notification.error("Something went wrong");
			return false;
		} finally {
			if (options?.closeQuickSettings) {
				this.setState({ quickSettingsSaving: false });
			}
		}
	};

	addQsPausedDate = () => {
		const draft = this.state.qsPauseDateDraft.trim();
		if (!/^\d{4}-\d{2}-\d{2}$/.test(draft)) {
			UI.notification.error("Choose a valid date.");
			return;
		}
		this.setState((s) => ({
			qsPausedDates: normalizeBookingPausedDates([...s.qsPausedDates, draft]),
			qsPauseDateDraft: "",
		}));
	};

	removeQsPausedDate = (date: string) => {
		this.setState((s) => ({
			qsPausedDates: s.qsPausedDates.filter((d) => d !== date),
		}));
	};

	saveQuickSettings = async () => {
		const { qsStrategy, qsThreshold, qsTurnMinutes, qsDashboardView, qsPausedDates } =
			this.state;
		const { store } = this.injected;
		const r = store.restaurant!;
		this.setState({ quickSettingsSaving: true });
		try {
			const bl = {
				...(r.settings.services.table_booking.booking_logic || {}),
				auto_confirm_strategy: qsStrategy as "off" | "always" | "smart",
				auto_confirm_threshold: qsThreshold !== "" ? Number(qsThreshold) : undefined,
				default_turn_minutes: qsTurnMinutes !== "" ? Number(qsTurnMinutes) : undefined,
				default_dashboard_view: qsDashboardView,
			};
			const paused_dates = normalizeBookingPausedDates(qsPausedDates);
			const res = await store.api.restaurant_update({
				_id: r._id,
				update: {
					$set: {
						"settings.services.table_booking.booking_logic": bl,
						"settings.services.table_booking.paused_dates": paused_dates,
					},
				},
			});
			if (res.outcome) {
				UI.notification.error(res.message || "Failed to save");
				return;
			}
			r.settings.services.table_booking.booking_logic = bl as any;
			r.settings.services.table_booking.paused_dates = paused_dates;
			UI.notification.success("Settings saved");
			this.setState({ quickSettingsOpen: false });
		} catch {
			UI.notification.error("Something went wrong");
		} finally {
			this.setState({ quickSettingsSaving: false });
		}
	};

	persistDashboardView = (view: BookingsDashboardView) => {
		void this.saveBookingLogic({ default_dashboard_view: view }, { notify: false });
	};

	handleBookingCreated = (booking: T.Schema.Booking.BookingSchema) => {
		const { store } = this.injected;
		const items = [...store.bookings.items, booking];
		const listSort = store.bookings.listSort ?? {
			sortKey: "config.timestamp",
			sortDirection: -1 as const,
		};
		const tz = store.restaurant?.settings.region.timezone;
		const sortedItems = tz
			? sortBookingsList(items, {
					...listSort,
					timezone: tz,
					dateFilter: store.bookings.dateFilter,
				})
			: items;
		store.updateBookings({
			items: sortedItems,
			count: (store.bookings.count || 0) + 1,
		});
		this.setState((s) => ({
			calendarReloadSignal: s.calendarReloadSignal + 1,
		}));
	};

	componentDidMount() {
		this.tryConsumeBookingPrefillFromStorage();
	}

	componentDidUpdate() {
		this.tryConsumeBookingPrefillFromStorage();
	}

	render() {
		const { store, t } = this.injected;
		const r = store.restaurant!;
		const rid = r._id;
		const items = store.bookings;
		const liveBookingsKey = items.items
			.map((b) => `${b._id}:${b.updated || b.created || b.config.timestamp}`)
			.join("|");
		const ri = r.reservations_integration;
		const masterOn = ri?.enabled === true;
		const calendarAllowed = masterOn && ri?.calendar_enabled !== false;
		const defaultDashboardView = resolveBookingsDashboardView(
			r.settings.services.table_booking.booking_logic?.default_dashboard_view,
			calendarAllowed
		);
		const {
			createModalOpen,
			walkInModalOpen,
			calendarReloadSignal,
			bookingGuestPrefill,
			calendarSlotPrefill,
		} = this.state;

		// Shared fetch + query config used by both the new and legacy list views
		const sharedQuery = {
			sortKeys: [
				{ label: "Booking Date", value: "config.timestamp" },
				{ label: "Placed", value: "created" },
				{ label: "Name", value: "customer.name" },
			],
			filters: [
				{
					label: "Status",
					key: "status",
					values: [
						{ label: "Un-Confirmed", value: "unconfirmed" },
						{ label: "Confirmed", value: "confirmed" },
						{ label: "Cancelled", value: "cancelled" },
					],
				},
			],
		};

		const sharedFetch = async (data: T.API.RestaurantQueryRequest) => {
			data.query.restaurant_id = store.restaurant!._id;
			const response = await store.api.bookings_find(data);
			if (response.outcome) throw new Error(response.message);
			const sortKey = Object.keys(data.sort)[0];
			const sortDirection = data.sort[sortKey] as 1 | -1;
			store.updateBookings({
				items: response.items,
				count: response.count,
				page: data.page,
				dateFilter: data.dateRange
					? { start: data.dateRange.start, end: data.dateRange.end }
					: undefined,
				listSort: { sortKey, sortDirection },
			});
		};

		const exportUrl = `${config.urls.api}/exports/dashboard/bookings?jwt=${store.auth.token}&rid=${store.restaurant!._id}`;

		const persistedFilters =
			store.bookings.tableFilters?.restaurantId === rid
				? store.bookings.tableFilters.state
				: undefined;

		const onPersistFilters = (state: any) =>
			store.updateBookings({ tableFilters: { restaurantId: rid, state } });

		return (
			<div>
				{/* ════════════════════════════════════════════════════════════
				    NEW UI — Reservations integration is ENABLED
				    ════════════════════════════════════════════════════════════ */}
				{masterOn && (
					<>
						{/* Page Header (Figma node 9:1115) */}
						<PageHeader>
							<HeaderLeft>
								<PageTitle>Bookings</PageTitle>
								<PageBreadcrumb>Home / Dashboard / Bookings</PageBreadcrumb>
							</HeaderLeft>

						{ri?.actions_enabled !== false && (
							<HeaderActions>
								<ActionBtn
									type="button"
									onClick={() =>
										this.setState({
											createModalOpen: true,
											calendarSlotPrefill: null,
										})
									}
								>
									<FaBookOpen style={{ fontSize: 14 }} />
									{t("Add booking", { defaultValue: "Add Booking" })}
								</ActionBtn>
								<ActionBtn
									type="button"
									onClick={() => this.setState({ walkInModalOpen: true })}
								>
									<FaUserPlus style={{ fontSize: 14 }} />
									{t("Walk-in", { defaultValue: "Add Walk-in" })}
								</ActionBtn>
								<ActionBtn
									type="button"
									title="Quick Settings"
									onClick={() => {
										const tb = store.restaurant?.settings?.services?.table_booking;
										const bl = tb?.booking_logic;
										this.setState({
											quickSettingsOpen: true,
											qsStrategy: bl?.auto_confirm_strategy || "off",
											qsThreshold: bl?.auto_confirm_threshold != null ? String(bl.auto_confirm_threshold) : "",
											qsTurnMinutes: bl?.default_turn_minutes != null ? String(bl.default_turn_minutes) : "",
											qsDashboardView: resolveBookingsDashboardView(
												bl?.default_dashboard_view,
												calendarAllowed
											),
											qsPausedDates: normalizeBookingPausedDates(tb?.paused_dates),
											qsPauseDateDraft: "",
										});
									}}
								>
									<FaCog style={{ fontSize: 14 }} />
									{t("Quick Settings", { defaultValue: "Quick Settings" })}
								</ActionBtn>
							</HeaderActions>
						)}
						</PageHeader>

						{/* Bookings Card (Figma node 9:975) */}
						<BookingsListView
							restaurantId={rid}
							persistedFilters={persistedFilters}
							onPersistFilters={onPersistFilters}
							region={r.settings.region}
							items={items.items}
							count={items.count}
							exportUrl={exportUrl}
							screenWidth={store.view.screen_width}
							query={sharedQuery}
							fetch={sharedFetch}
							onClick={(item) => {
								store.router.push(`/restaurant/${rid}/bookings?_id=${item._id}`);
								this.setState({ item });
								store.notifications.mark_read_object("booking", item._id);
							}}
							calendarAllowed={calendarAllowed}
							defaultDashboardView={defaultDashboardView}
							onDashboardViewChange={(view) => {
								this.setState({ qsDashboardView: view });
								this.persistDashboardView(view);
							}}
							calendarReloadSignal={calendarReloadSignal}
							liveBookings={items.items}
							liveBookingsKey={liveBookingsKey}
							suppressCalendarSelection={createModalOpen || walkInModalOpen}
							onRequestCreateAtSlot={(slot) =>
								this.setState({
									createModalOpen: true,
									calendarSlotPrefill: slot,
									bookingGuestPrefill: null,
								})
							}
						/>
					</>
				)}

				{/* ════════════════════════════════════════════════════════════
				    LEGACY UI — Reservations integration is DISABLED
				    ════════════════════════════════════════════════════════════ */}
				{!masterOn && (
					<>
						<div className="flex-l-r-center m-b-4 flex-wrap" style={{ gap: "1rem" }}>
							<h1 className="m-b-0">Bookings</h1>
						</div>

						<BookingsProtable<T.Schema.Booking.BookingSchema>
							restaurantId={rid}
							persistedFilters={persistedFilters}
							onPersistFilters={onPersistFilters}
							region={r.settings.region}
							items={items.items}
							count={items.count}
							exportUrl={exportUrl}
							screenWidth={store.view.screen_width}
							query={sharedQuery}
							fetch={sharedFetch}
							onClick={(item) => {
								store.router.push(`/restaurant/${rid}/bookings?_id=${item._id}`);
								this.setState({ item });
								store.notifications.mark_read_object("booking", item._id);
							}}
							rowAlert={(order) => order.status === "unconfirmed"}
							columns={[
								{
									title: "",
									width: 80,
									render: (row) => (
										<IconCircle
											className="center"
											size={38}
											icon={<OrderIcon service="table_booking" />}
											background={RestaurantUtils.order.color(row.status)}
										/>
									),
								},
								{
									title: "# / Name",
									render: (row) => (
										<div>
											<p className="big font-semi-bold m-b-1">{row.number}</p>
											<p>{row.customer.name}</p>
										</div>
									),
								},
								{
									title: "Phone",
									breakpoint: 1000,
									render: (row) => <span>{row.customer.phone}</span>,
								},
								{
									title: "Email",
									breakpoint: 1100,
									render: (row) => <span>{row.customer.email}</span>,
								},
								{
									title: "Booking For",
									breakpoint: 520,
									render: (row) => (
										<span>{t("datetimeFromTimestamp", { value: row.config.timestamp })}</span>
									),
								},
								{
									title: "Placed",
									breakpoint: 620,
									render: (row) => <RelativeTime timestamp={row.created} />,
								},
								{
									title: "No. Of People",
									breakpoint: 720,
									render: (row) => (
										<span>{t("number", { value: row.config.number_of_people })}</span>
									),
								},
								{
									title: "Status",
									breakpoint: 820,
									render: (row) => <span>{t(`order.status.${row.status}`)}</span>,
								},
							]}
						/>
					</>
				)}

				<RestaurantBookingModal />

				{/* Quick Settings Modal */}
				{this.state.quickSettingsOpen && (
					<QSBackdrop onClick={(e) => { if (e.target === e.currentTarget) this.setState({ quickSettingsOpen: false }); }}>
						<QSCard onClick={(e) => e.stopPropagation()}>
							<QSHeader>
								<FaCog style={{ fontSize: 18, color: "#6b7280", flexShrink: 0 }} />
								<QSTitle>Quick Settings</QSTitle>
								<QSClose type="button" onClick={() => this.setState({ quickSettingsOpen: false })}>×</QSClose>
							</QSHeader>
							<QSBody>
								<QSField>
									<QSLabel>Auto-Confirmation Strategy</QSLabel>
									<QSSelect
										value={this.state.qsStrategy}
										onChange={(e) => this.setState({ qsStrategy: e.target.value })}
									>
										<option value="off">Off (manual confirm)</option>
										<option value="always">Always auto-confirm</option>
										<option value="smart">Smart (threshold)</option>
									</QSSelect>
									<QSHelp>Controls automatic confirmation when limits allow.</QSHelp>
								</QSField>
								{this.state.qsStrategy === "smart" && (
									<QSField>
										<QSLabel>Smart Capacity Threshold (%)</QSLabel>
										<QSInput
											type="number"
											min={1}
											max={100}
											placeholder="e.g. 80"
											value={this.state.qsThreshold}
											onChange={(e) => this.setState({ qsThreshold: e.target.value })}
										/>
										<QSHelp>Auto-confirms below this booked-capacity %; at or above it stays unconfirmed.</QSHelp>
									</QSField>
								)}
								{calendarAllowed && (
									<QSField>
										<QSLabel>Default Dashboard View</QSLabel>
										<QSSelect
											value={this.state.qsDashboardView}
											onChange={(e) =>
												this.setState({
													qsDashboardView: e.target.value as BookingsDashboardView,
												})
											}
										>
											<option value="list">List View</option>
											<option value="calendar">Calendar View</option>
										</QSSelect>
										<QSHelp>Opens on this tab when you return to Bookings. Switching tabs saves automatically.</QSHelp>
									</QSField>
								)}
								<QSField>
									<QSLabel>Default Turn Length (minutes)</QSLabel>
									<QSInput
										type="number"
										min={15}
										step={15}
										placeholder="e.g. 90"
										value={this.state.qsTurnMinutes}
										onChange={(e) => this.setState({ qsTurnMinutes: e.target.value })}
									/>
									<QSHelp>Used for table overlap checks, auto-assignment, and calendar defaults.</QSHelp>
								</QSField>
								<QSField>
									<QSLabel>Pause online bookings (by date)</QSLabel>
									<div style={{ display: "flex", gap: 8, alignItems: "center" }}>
										<QSInput
											type="date"
											value={this.state.qsPauseDateDraft}
											onChange={(e) =>
												this.setState({ qsPauseDateDraft: e.target.value })
											}
										/>
										<QSBtn type="button" onClick={this.addQsPausedDate}>
											Add
										</QSBtn>
									</div>
									<QSHelp>
										Guests cannot book on these days on your website or embed. Staff can
										still add bookings and walk-ins in the dashboard.
									</QSHelp>
									{this.state.qsPausedDates.length > 0 ? (
										<ul
											style={{
												margin: "8px 0 0",
												padding: 0,
												listStyle: "none",
												display: "flex",
												flexDirection: "column",
												gap: 6,
											}}
										>
											{this.state.qsPausedDates.map((d) => (
												<li
													key={d}
													style={{
														display: "flex",
														alignItems: "center",
														justifyContent: "space-between",
														fontSize: 14,
														color: "#414651",
													}}
												>
													<span>{d}</span>
													<button
														type="button"
														onClick={() => this.removeQsPausedDate(d)}
														style={{
															border: "none",
															background: "none",
															color: "#b42318",
															cursor: "pointer",
															fontSize: 13,
														}}
													>
														Remove
													</button>
												</li>
											))}
										</ul>
									) : null}
								</QSField>
							</QSBody>
							<QSFooter>
								<QSBtn type="button" onClick={() => this.setState({ quickSettingsOpen: false })}>Cancel</QSBtn>
								<QSBtn type="button" $primary disabled={this.state.quickSettingsSaving} onClick={this.saveQuickSettings}>
									{this.state.quickSettingsSaving ? "Saving…" : "Save"}
								</QSBtn>
							</QSFooter>
						</QSCard>
					</QSBackdrop>
				)}

				<RestaurantBookingCreateModal
					open={createModalOpen}
					bookingGuestPrefill={bookingGuestPrefill}
					calendarSlotPrefill={calendarSlotPrefill}
					onClose={() =>
						this.setState({
							createModalOpen: false,
							bookingGuestPrefill: null,
							calendarSlotPrefill: null,
						})
					}
					onCreated={(booking) => this.handleBookingCreated(booking)}
				/>

				<RestaurantWalkInModal
					open={walkInModalOpen}
					onClose={() => this.setState({ walkInModalOpen: false })}
					onCreated={(booking) => {
						this.handleBookingCreated(booking);
						this.setState({ walkInModalOpen: false });
					}}
				/>

			</div>
		);
	}

}

export const RestaurantBookings = withTranslation()(RestaurantBookingsClass);
